<template>
  <div class="tenancy-tenant-view-info-wrapper">
    <div class="main-content">
      <div class="basic-info-column">
        <div class="column-header">
          {{ $t('tenancy.basicInfo') }}
        </div>
        <div class="contents">
          <div class="column-content">
            <TextField
              :label=" $t('tenancy.companyId')"
              :value="deep(currentTenancy, 'company.id')"
              :showColon="false"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.companyName')"
              :value="deep(currentTenancy, 'company.name')"
              :showColon="false"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.tradeName')"
              :value="currentTenancy.tradeName || '--'"
              :showColon="false"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.phoneNumber')"
              :value="deep(currentTenancy, 'company.phone') || '--'"
              :showColon="false"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.alternativePhoneNumber')"
              :value="deep(currentTenancy, 'company.altPhone') || '--'"
              :showColon="false"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.faxNumber')"
              :value="deep(currentTenancy, 'company.fax') || '--'"
              :showColon="false"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.email')"
              :value="deep(currentTenancy, 'company.email') || '--'"
              :showColon="false"
              :readonly="true"
            />
          </div>
          <div class="column-content column-content-right">
            <TextField
              :label=" $t('tenancy.building')"
              :value="deep(currentTenancy, 'building.fullName') || '--'"
              :showColon="false"
              class="building"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.floor')"
              :value="effectiveFloors"
              :showColon="false"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.type')"
              :value="tenancyTypeValue"
              :showColon="false"
              :readonly="true"
            />
            <div
              v-if="!showHolidayHour"
              class="tenancy-working-hours"
            >
              <label>{{ $t('tenancy.openingHours') }}</label>
              <ul
                v-if="regularOpeningHours.length"
                class="tenancy-working-hours-list"
              >
                <li
                  v-for="(item, index) in regularOpeningHours"
                  :key="index"
                >
                  <span>{{ item.workingDayFrom | customWorkingDayLabel }}</span><span v-if="item.workingDayTo"> - </span><span class="u-margin-right-small">{{ item.workingDayTo | customWorkingDayLabel }}</span>
                  <span>{{ item.workingHourFrom | customWorkingHourLabel }}</span><span v-if="item.workingHourTo"> - </span><span>{{ item.workingHourTo | customWorkingHourLabel }}</span>
                </li>
              </ul>
              <div v-else>
                {{ $t('emptyValue') }}
              </div>
            </div>
            <div
              v-if="isOffice && enableDirectoryFeature"
              class="directory-link"
            >
              <a @click="navToDirectory">{{ $t('directory.requisition') }}</a>
            </div>
          </div>
        </div>
        <TenancyOpeningHour v-if="showHolidayHour" />
        <div class="alarm-section">
          <div class="alarm-section__label-btn">
            <label>{{ $t('tenancy.tenantAlarmSysHint') }}</label>
            <div
              v-if="canUpdateAlarm && isMaster"
              class="alarm-section__btn-group"
            >
              <i
                v-show="!isEditAlarm"
                class="fa fa-pencil"
                @click="isEditAlarm=true"
              />
              <button
                v-if="!isEditAlarm"
                role="edit-button"
                class="alarm-section__btn btn--withoutcolor"
                @click="isEditAlarm=true"
              >
                {{ $t('action.edit') }}
              </button>
              <button
                v-else
                role="save-button"
                class="alarm-section__btn btn--withoutcolor"
                @click="saveAlarm()"
              >
                {{ $t('action.save') }}
              </button>
            </div>
          </div>

          <div class="fire-alarm-section">
            <label class="emphasis alarm-label">{{ $t('tenancy.fireAlarmSystem') }}:</label>
            <input
              v-hkldinput
              class="alarm-checkbox"
              type="radio"
              name="fireAlarm"
              :disabled="!isEditAlarm"
              :checked="currentTenancy.isFireAlarm"
              @change="updateTenancyByPath('isFireAlarm', true)"
            >
            <label class="alarm-checkbox-label">{{ $t('action.yes') }}</label>
            <input
              v-hkldinput
              class="alarm-checkbox"
              type="radio"
              name="fireAlarm"
              :disabled="!isEditAlarm"
              :checked="currentTenancy.isFireAlarm !== null && !currentTenancy.isFireAlarm"
              @change="updateTenancyByPath('isFireAlarm', false)"
            >
            <label class="alarm-checkbox-label">{{ $t('action.no') }}</label>
          </div>
          <div>
            <label class="emphasis alarm-label">{{ $t('tenancy.burglarAlarmSystem') }}:</label>
            <input
              v-hkldinput
              type="radio"
              name="burglarAlarm"
              :disabled="!isEditAlarm"
              :checked="currentTenancy.isBurglarAlarm"
              @change="updateTenancyByPath('isBurglarAlarm', true)"
            >
            <label class="alarm-checkbox-label">{{ $t('action.yes') }}</label>
            <input
              v-hkldinput
              type="radio"
              name="burglarAlarm"
              :disabled="!isEditAlarm"
              :checked="currentTenancy.isBurglarAlarm !== null && !currentTenancy.isBurglarAlarm"
              @change="updateTenancyByPath('isBurglarAlarm', false)"
            >
            <label class="alarm-checkbox-label">{{ $t('action.no') }}</label>
          </div>
        </div>
      </div>
      <div class="building-management-column">
        <div class="column-header">
          {{ $t('tenancy.hkldInformation') }}
          <div class="sow-hint">
            <Popover
              :text="$t('tenancy.hkldInfoTip')"
              position="bottom-right"
              :width="300"
            />
          </div>
        </div>

        <div class="column-content">
          <TextField
            :label=" $t('tenancy.customerServiceHotline')"
            :value="deep(currentTenancy, 'physicalSite.officeTelephone') || '--'"
            :showColon="false"
            :readonly="true"
          />

          <div class="location-contacts">
            <TenancyLocationContacts
              :title="$t('tenancy.propertyManagement')"
              :contacts="propertyManagementContacts"
            />
            <TenancyLocationContacts
              :title="$t('tenancy.technicalServices')"
              :contacts="technicalServicesContacts"
            />
            <TenancyLocationContacts
              :title="$t('tenancy.assetManagement')"
              :contacts="assetManagementContacts"
            />
          </div>

          <TextField
            :label=" $t('tenancy.manageOfficeAddr')"
            :value="deep(currentTenancy, 'physicalSite.officeLocation') || '--'"
            :showColon="false"
            :readonly="true"
          />
          <div class="regulations-links">
            <a
              v-for="link in redirectLinksList"
              :key="link.key"
              :href="link.value"
              target="_blank"
              class="name-hyper-link"
            >
              {{ $t(`tenancy.${link.key}`) }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showHotbList"
      class="hotb-section"
    >
      <HotbList :tenancyId="tenancyId" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import lodash from 'lodash'

import HotbList from '@/modules/hotb/HotbList'
import Popover from '@/controls/Popover'
import TextField from '@/controls/TextField'
import TenancyLocationContacts from './TenancyLocationContacts'
import TenancyOpeningHour from '@/modules/ccd/tenancy/view/TenancyOpeningHour'

import * as userUtil from '@/api/users/util'
import mutationTypes from '@/store/mutation-types'
import navigation from '@/utils/navigation'
import { aclHelper, tenantPermsTypes } from '@/utils/aclHelper'
import { getTenancyFloors } from '@/api/tenancies/util'
import { hotbStatus, hotbTypes, tenancyType } from '@/utils/locale'
import { locationContactsDepartment, directoryRouteNames } from '@/utils/consts'
import { setTenancyInfoForDirectory } from '@/modules/directories/utils'
import { utils as formUtils } from '@/plugin/InputChange'
import { getLocationContacts } from '@/api/locations/request'

export default {
  name: 'TenancyTenantInformation',
  components: {
    HotbList,
    Popover,
    TextField,
    TenancyLocationContacts,
    TenancyOpeningHour,

  },
  props: {
    tenancyId: {
      type: [String, Number],
      default: undefined,
    },
  },
  data () {
    return {
      deep,
      hotbTypes,
      hotbStatus,
      isEditAlarm: false,
      tenancyType,
      managementContacts: [],
    }
  },
  computed: {
    ...mapGetters('settings', ['redirectLinksList']),
    ...mapGetters('ccd', ['canAccessAllHotbs', 'currentTenancy', 'isMaster']),
    ...mapGetters('regions', ['backEndRegionalEnv', 'enableDirectoryFeature', 'enableTenantProjectsFeature']),
    propertyManagementContacts () {
      const result = this.filterByDepartment(locationContactsDepartment.propertyManagement)
      return result
    },
    showHotbList () {
      return this.enableTenantProjectsFeature && this.canAccessAllHotbs
    },
    assetManagementContacts () {
      const result = this.filterByDepartment(locationContactsDepartment.assetManagement)
      return result
    },
    technicalServicesContacts () {
      const result = this.filterByDepartment(locationContactsDepartment.technicalServices)
      return result
    },
    effectiveFloors () {
      return getTenancyFloors(this.currentTenancy, true).join(', ') || this.$t('emptyValue')
    },
    canUpdateAlarm () {
      return aclHelper.getAccess(tenantPermsTypes.tenancies.readUpdate)
    },
    isOffice () {
      return deep(this.currentTenancy, 'type') === tenancyType.Office
    },
    showHolidayHour () {
      return !this.isOffice && lodash.get(this.backEndRegionalEnv, 'moduleSwitch.holidayWorkingHours')
    },
    regularOpeningHours () {
      return lodash.get(this.currentTenancy, 'openingHours.regularWorkingHours') || []
    },
    tenancyTypeValue () {
      const localeKey = this.currentTenancy.type && this.currentTenancy.type.toLowerCase()
      return localeKey ? this.$t(`tenancy.${localeKey}`) : this.$t('emptyValue')
    },
  },
  async created () {
    await this.loadRedirectLinks()
  },
  async mounted () {
    this.managementContacts = await this.getManagementContacts()
  },
  methods: {
    ...mapActions('ccd', ['updateTenancy']),
    ...mapActions('settings', ['loadRedirectLinks']),
    ...mapMutations('ccd', {
      updateTenancyPathField: mutationTypes.TENANCY_UPDATE_PATH_FIELD,
    }),
    ...mapMutations('directories', {
      setTenancyInfo: mutationTypes.DIRECTORY_TENANCY_INFO,
    }),
    navToDirectory () {
      this.setTenancyInfo(
        setTenancyInfoForDirectory(this.currentTenancy))
      navigation.toPage(directoryRouteNames.tenantDirectories, { id: this.currentTenancy.id })
    },
    updateTenancyByPath (path, val) {
      this.updateTenancyPathField({
        path: [path],
        newValue: val,
      })
    },
    async saveAlarm () {
      this.isEditAlarm = false
      await this.updateTenancy()
      formUtils.resetDirty()
    },
    filterByDepartment (department) {
      return this.managementContacts.filter(item => item.department === department)
    },
    async getManagementContacts () {
      let result = []
      const physicalSite = lodash.get(this.currentTenancy, 'physicalSite', null)
      if (physicalSite) {
        try {
          const contactsResult = await getLocationContacts(physicalSite.id)
          result = contactsResult.map(contact => {
            return {
              ...contact,
              name: contact.user.displayName,
              username: contact.user.username,
              email: userUtil.getPrimaryEmail(contact.user),
            }
          })
        } catch (error) {
          result = []
        }
      }
      return result
    },
  },
}
</script>

<style lang="scss" >
@import '~styles/tender/view';
@import '~styles/components/tenancy-view-info';
@import "~styles/stickyBtn";
</style>
<style lang="scss">
.tenancy-tenant-view-info-wrapper {
  .tenancy-working-hours-list {
    list-style: none;
    margin-left: 0;
  }
  .column-header {
    height: 48px;
    min-height: 48px;
    line-height: 48px;
    padding-left: 15px;
    font-size: 16px;
    font-weight: bold;
    box-shadow: 0 2px 4px 0 rgba(212, 212, 212, 0.5);
  }
  .sow-hint {
    display: inline-block;
    color: #6e7a91;
    font-size: 14px;
    font-family: Verdana;
    font-style: normal;
    font-weight: normal;
  }
  .main-content {
    display: flex;
    min-height: 400px;
    div.text-field label {
      height: 20px;
      padding: 0 0 5px 0;
      margin: 0;
      font-size: 14px;
    }

    .basic-info-column {
      flex: 1;
      background: white;
      box-shadow: 0 2px 4px 0 rgba(212, 212, 212, 0.5);
      flex-direction: column;
      .contents {
        padding: 15px;
        flex: 1;
        display: flex;
        p {
          word-break: break-word;
        }
        .column-content {
          flex: 1;
          flex: 1;
        }
      }
      .alarm-section {
        padding: 15px;
        padding-top: 5px;
        border-top: 1px solid #f1f1f1;
        &__label-btn {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          margin-bottom: 10px;
        }
        .alarm-label {
          display: inline;
          margin-right: 10px;
          font-weight: normal;
        }
        .alarm-checkbox-label {
          margin: 0px;
          font-weight: normal;
        }
      }
    }
    .column-content{
      padding: 0;
      margin: 0;
      .tenancy-working-hours {
        label {
          margin-top: 0;
        }
      }
    }
    .column-content-right {
      padding-left: 15px;
    }
    .building-management-column {
      flex: 1;
      margin-left: 15px;
      background: white;
      display: flex;
      flex-direction: column;
      box-shadow: 0 2px 4px 0 rgba(212, 212, 212, 0.5);
      position: relative;
      .column-content {
        padding: 15px;
        flex: 1;
      }
      .regulations-links {
        position: absolute;
        bottom: 25px;
        line-height: 2;
        font-weight: bold;
        > a {
          display: block;
        }
      }
    }
  }
  .hotb-section {
    margin-top: 20px;
    background-color: #ffffff;
    width: 100%;
    overflow: hidden;
  }

  .location-contacts {
    margin-top: 3px;
  }

  .directory-link {
    margin-top: 10px;
    a {
      text-decoration: underline;
      font-weight: bold;
    }
  }
}
</style>
